import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const SHOTS = [
  { src: "/screens/share-card.jpeg", caption: "Your DM personality card", alt: "DMWrapped share card showing The Night Owl personality with top 3 people" },
  { src: "/screens/inner-circle.jpeg", caption: "Your inner circle", alt: "Inner circle screen ranking the people you text the most" },
  { src: "/screens/deep-dive.jpeg", caption: "Deep dive on any person", alt: "Deep dive screen showing who talks more, who texts first and reply speed" },
  { src: "/screens/head-to-head.jpeg", caption: "Head-to-head battles", alt: "Head-to-head screen comparing two friends on messages and compatibility" },
  { src: "/screens/verdicts.jpeg", caption: "Savage DM verdicts", alt: "Verdicts screen showing Ghost-in-Chief, Night Crawler and Emoji Goblin" },
  { src: "/screens/group-chats.jpeg", caption: "Who carries the group chat", alt: "Group chats screen ranking who carries each group chat" },
  { src: "/screens/cover.jpeg", caption: "Your report cover", alt: "Report cover screen saying Your DMs, wrapped with message totals" },
  { src: "/screens/home.jpeg", caption: "Your DM home base", alt: "DMWrapped home screen with streak and steps to get your report" },
  { src: "/screens/unlock.jpeg", caption: "Unlock the full report", alt: "Unlock full report screen listing reply-time score, heatmap and more" },
];

export function Screenshots() {
  const trackRef = useRef(null);

  // Auto-rotate the carousel whenever it overflows; pauses on touch, resumes after 6s idle.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let paused = false;
    let resumeT;
    const timer = setInterval(() => {
      if (paused) return;
      if (el.scrollWidth <= el.clientWidth + 8) return;
      const card = el.querySelector("figure");
      const step = card ? card.offsetWidth + 24 : 260;
      const max = el.scrollWidth - el.clientWidth;
      const next = el.scrollLeft + step;
      el.scrollTo({ left: next >= max - 8 ? 0 : next, behavior: "smooth" });
    }, 3000);
    const pause = () => {
      paused = true;
      clearTimeout(resumeT);
      resumeT = setTimeout(() => { paused = false; }, 6000);
    };
    el.addEventListener("pointerdown", pause);
    return () => {
      clearInterval(timer);
      clearTimeout(resumeT);
      el.removeEventListener("pointerdown", pause);
    };
  }, []);

  return (
    <section id="screenshots" data-testid="screenshots-section" className="scroll-mt-24 overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-acid"
        >
          Inside the app
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-paper sm:text-4xl lg:text-5xl"
        >
          This is what your DMs look like, wrapped
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
          className="mt-4 max-w-xl text-[15px] leading-relaxed text-dim"
        >
          Real screens from the app — your personality card, savage verdicts, deep dives on your
          favourite people, head-to-head battles, and who really carries the group chat.
        </motion.p>
      </div>

      <div className="mt-14">
        <div
          ref={trackRef}
          data-testid="screenshots-track"
          className="flex gap-6 overflow-x-auto px-6 pb-8 pt-4 snap-x snap-mandatory sm:px-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {SHOTS.map((s, i) => (
            <motion.figure
              key={s.src}
              data-testid={`screenshot-${i}`}
              initial={{ opacity: 0, y: 48, rotate: 0 }}
              whileInView={{ opacity: 1, y: 0, rotate: i % 2 === 0 ? -2 : 2 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: Math.min(i, 4) * 0.1, ease: EASE }}
              whileHover={{ rotate: 0, y: -10, scale: 1.03 }}
              className={`group w-[240px] shrink-0 snap-center sm:w-[250px] ${i % 2 === 0 ? "lg:mt-10" : ""}`}
            >
              <div className="overflow-hidden rounded-[1.75rem] border border-white/[0.1] bg-panel shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] transition-colors duration-300 group-hover:border-acid/40">
                <img src={s.src} alt={s.alt} loading="lazy" className="block w-full" />
              </div>
              <figcaption className="mt-3 text-center text-[13px] font-medium text-dim">
                {s.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
